import { useEffect, useState } from "react";
import { loadSupport } from "@/lib/site";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CustomerProvider, useCustomer } from "@/context/CustomerContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Portfolio from "@/pages/Portfolio";
import CreateAd from "@/pages/CreateAdOrder";
import Login from "@/pages/account/Login";
import Register from "@/pages/account/Register";
import { ForgotPassword, ResetPassword, ChangeTempPassword } from "@/pages/account/PasswordReset";
import AccountDashboard from "@/pages/account/AccountDashboard";
import OrderDetails from "@/pages/account/OrderDetails";
import WalletPage from "@/pages/account/WalletPage";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PublicLayout = () => (
  <div className="km-grain min-h-screen">
    <Header />
    <main id="main">
      <Outlet />
    </main>
    <Footer />
    <FloatingWhatsApp />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-[var(--km-lilac)]" data-testid="admin-loading">بنحمّل...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

const CustomerOnly = ({ children }) => {
  const { customer } = useCustomer();
  const { pathname, search } = useLocation();
  if (customer === null) return <div className="grid min-h-[50vh] place-items-center text-[var(--km-lilac)]" data-testid="customer-loading">بنحمّل...</div>;
  if (!customer) return <Navigate to={`/login?next=${encodeURIComponent(pathname + search)}`} replace />;
  if (customer.must_change_password && pathname !== "/account/change-password") return <Navigate to="/account/change-password" replace />;
  return children;
};

function App() {
  const [, setSupportReady] = useState(false);
  useEffect(() => {
    loadSupport().then(() => setSupportReady(true));
  }, []);
  return (
    <div dir="rtl" lang="ar">
      <BrowserRouter>
        <AuthProvider>
          <CustomerProvider>
          <ScrollTop />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/create-ad" element={<CreateAd />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={<CustomerOnly><AccountDashboard /></CustomerOnly>} />
              <Route path="/account/orders/:id" element={<CustomerOnly><OrderDetails /></CustomerOnly>} />
              <Route path="/account/change-password" element={<CustomerOnly><ChangeTempPassword /></CustomerOnly>} />
              <Route path="/wallet" element={<CustomerOnly><WalletPage /></CustomerOnly>} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
          </Routes>
          </CustomerProvider>
        </AuthProvider>
        <Toaster position="top-center" richColors dir="rtl" toastOptions={{ style: { fontFamily: "Tajawal" } }} />
      </BrowserRouter>
    </div>
  );
}

export default App;
